import { createContext, useContext, useEffect, useMemo, useState } from "react";

const LOCALES = ["en", "ar", "es"];
const STORAGE_KEY = "pz_lang";

const dict = {
  en: {
    common: {
      lang_en: "EN",
      lang_ar: "AR",
      switch_to_arabic: "Switch to Arabic",
      switch_to_english: "Switch to English",
      request_quote: "Request a Quote",
      contact_us: "Contact Us",
      whatsapp: "Chat on WhatsApp",
    },
    nav: {
      home: "Home",
      about: "About",
      products: "Products",
      brands: "Brands",
      why: "Why Prime Zone",
      process: "Process",
      contact: "Contact",
    },
    brand: {
      name_prefix: "Prime Zone",
      name_suffix: "FZCO",
    },
    hero: {
      eyebrow: "Dubai-Based · Globally Connected",
      headline_a: "Wholesale",
      headline_b: "Mobile Phones",
      headline_c: "& Accessories Trading",
      headline_d: "from Dubai to the World.",
      subheadline:
        "Prime Zone FZCO connects wholesale traders with reliable mobile phones, accessories, and global trading opportunities — shipping from Dubai to international markets.",
      badges: [
        "Dubai Airport Freezone",
        "Global Shipping",
        "B2B Wholesale Trading",
        "Multi-Brand Mobile Phones",
        "Accessories Supply",
      ],
      stat_origin: { label: "Origin", value: "Dubai, UAE" },
      stat_reach: { label: "Reach", value: "40+ Markets" },
      stat_routes: { label: "Shipping", value: "Global Routes" },
      hub_label: "Hub · Dubai",
    },
    about: {
      eyebrow: "About Prime Zone",
      title_a: "Built for serious wholesale",
      title_b: "mobile trade",
      title_dot: ".",
      subtitle:
        "Prime Zone FZCO is a Dubai Airport Freezone company specialized in the wholesale trading of mobile phones and accessories. We work with traders, resellers, distributors, and business partners who require reliable sourcing, competitive market opportunities, and professional B2B service.",
      paragraph:
        "Our focus is on fast communication, market availability, and trusted trading support for wholesale clients looking to buy or sell mobile phones and accessories.",
      cta: "Start a wholesale conversation",
      cards: [
        {
          title: "Dubai Airport Freezone Company",
          body: "A registered Freezone entity based at Dubai Airport — built for fast, internationally connected wholesale trade.",
        },
        {
          title: "Mobile Phones Trading",
          body: "Sourcing and trading of mobile phones across major global brands.",
        },
        {
          title: "Accessories Trading",
          body: "Wholesale supply of chargers, cables, cases and protection.",
        },
        {
          title: "Wholesale B2B Focus",
          body: "Built for traders, resellers, distributors and bulk buyers.",
        },
        {
          title: "UAE & International Business",
          body: "Serving partners across the UAE and major international markets.",
        },
      ],
    },
    products: {
      eyebrow: "What We Trade",
      title_a: "Categories built for",
      title_b: "wholesale traders",
      title_dot: ".",
      subtitle:
        "We support wholesale trading across mobile phones, accessories, and related product categories based on market availability and client requirements.",
      cards: [
        {
          title: "Mobile Phones",
          body: "Wholesale trading of mobile phones from major global brands based on availability.",
        },
        {
          title: "Smartphones",
          body: "Sourcing and trading of new smartphone models for B2B clients and resellers.",
        },
        {
          title: "Mobile Accessories",
          body: "Accessories supply for traders, shops, and distributors.",
        },
        {
          title: "Chargers & Cables",
          body: "Wholesale trading of charging accessories and mobile connectivity products.",
        },
        {
          title: "Cases & Protection",
          body: "Phone cases, screen protectors, and related protection accessories.",
        },
        {
          title: "Bulk Orders",
          body: "Support for wholesale quantity requirements and business inquiries.",
        },
        {
          title: "Trading Support",
          body: "Professional communication and sourcing support for mobile traders.",
        },
        {
          title: "Market Availability",
          body: "Product availability depends on current market supply and trading conditions.",
        },
      ],
    },
    brands: {
      eyebrow: "Multi-Brand Mobile Trading",
      title_a: "Trading across",
      title_b: "global brands",
      title_dot: ".",
      subtitle:
        "Prime Zone FZCO trades across major global mobile phone brands and accessories depending on market availability, client requirements, and wholesale supply conditions.",
      other: "Other Global Brands",
      disclaimer:
        "Brand names and logos are shown for trading category reference only. Availability may vary based on market supply. Prime Zone FZCO does not claim official distributorship unless specifically stated. All trademarks belong to their respective owners.",
    },
    why: {
      eyebrow: "Why Choose Prime Zone",
      title_a: "A professional",
      title_b: "B2B trading partner",
      title_c: "from Dubai.",
      subtitle:
        "Reliable, fast, and internationally connected — engineered for mobile phones and accessories wholesale.",
      cards: [
        {
          title: "Dubai Freezone Company",
          body: "Located in Dubai Airport Freezone, supporting regional and international business activity.",
        },
        {
          title: "Wholesale-Focused",
          body: "Built for traders, resellers, distributors, and bulk business requirements.",
        },
        {
          title: "Multi-Brand Trading",
          body: "Access to mobile phone trading opportunities across major brands based on availability.",
        },
        {
          title: "Accessories Supply",
          body: "Supporting wholesale needs for mobile accessories and related products.",
        },
        {
          title: "Fast Communication",
          body: "Clear, direct, and professional communication for business clients.",
        },
        {
          title: "Competitive Market Deals",
          body: "Focused on market-driven trading opportunities and wholesale value.",
        },
        {
          title: "Reliable Sourcing Network",
          body: "Helping clients find suitable stock and trading opportunities.",
        },
        {
          title: "UAE & International Reach",
          body: "Serving local and international wholesale buyers and partners.",
        },
      ],
    },
    process: {
      eyebrow: "How We Work",
      title_a: "A simple, professional",
      title_b: "inquiry process",
      title_dot: ".",
      subtitle:
        "Five clean steps from inquiry to delivery — designed for serious wholesale buyers.",
      steps: [
        {
          title: "Share Your Requirement",
          body: "Send us the product type, brand, model, and quantity you need.",
        },
        {
          title: "Availability Check",
          body: "We check market availability and suitable trading options.",
        },
        {
          title: "Receive Quotation",
          body: "We provide pricing and business terms based on your inquiry.",
        },
        {
          title: "Confirm Order",
          body: "Once agreed, the order details and payment terms are confirmed.",
        },
        {
          title: "Delivery or Collection",
          body: "We coordinate collection, delivery, or shipment arrangements based on the deal.",
        },
      ],
    },
    cta: {
      badge: "Wholesale Inquiry",
      title_a: "Ready to",
      title_b: "Trade?",
      body: "Connect with Prime Zone FZCO for wholesale mobile phones and accessories trading inquiries.",
      whatsapp_btn: "Contact Us on WhatsApp",
    },
    contact: {
      eyebrow: "Let's Connect",
      title_a: "Send your",
      title_b: "wholesale inquiry",
      title_dot: ".",
      subtitle:
        "Tell us what you need — our team will review and reply with availability, pricing and terms.",
      company_block: {
        name: "Prime Zone FZCO",
        tagline: "Wholesale Mobile Phones & Accessories Trading",
      },
      labels: {
        location: "Location",
        phone: "Phone / WhatsApp",
        email: "Email",
      },
      address:
        "6EA 121, First Floor, Building 6 East A,\nDubai Airport Freezone, Dubai, UAE",
      fields: {
        full_name: "Full Name",
        company_name: "Company Name",
        country: "Country",
        phone: "Phone / WhatsApp",
        email: "Email Address",
        product_requirement: "Product Requirement",
        brand_model: "Brand / Model",
        quantity: "Quantity",
        message: "Message",
        message_placeholder:
          "Tell us about your requirement, target market or any specifics.",
      },
      submit: "Submit Inquiry",
      submitting: "Submitting…",
      consent:
        "By submitting, you agree to be contacted by Prime Zone FZCO regarding your business inquiry.",
      toast_success:
        "Inquiry submitted. Our team will get back to you shortly.",
      toast_validation: "Please fill in your name, email and phone.",
      toast_error: "Failed to submit inquiry. Please try again.",
      map_caption:
        "6EA 121, First Floor, Building 6 East A · Dubai Airport Freezone, Dubai, UAE",
      directions: "Open in Google Maps →",
    },
    footer: {
      tagline: "Wholesale Mobile Phones & Accessories Trading.",
      address_l1: "6EA 121, First Floor, Building 6 East A,",
      address_l2: "Dubai Airport Freezone, Dubai, UAE.",
      cols: {
        company: "Company",
        trading: "Trading",
        contact: "Contact",
      },
      links: {
        about: "About",
        products: "Products",
        brands: "Brands",
        why: "Why Prime Zone",
        mobile_phones: "Mobile Phones",
        smartphones: "Smartphones",
        accessories: "Accessories",
        bulk_orders: "Bulk Orders",
        request_quote: "Request a Quote",
        whatsapp: "WhatsApp",
        email: "Email",
        location: "Location",
      },
      copyright: "© 2026 Prime Zone FZCO. All rights reserved.",
      built_for: "Built for B2B wholesale trade.",
    },
  },
  ar: {
    common: {
      lang_en: "EN",
      lang_ar: "AR",
      lang_es: "ES",
      switch_to_arabic: "التبديل إلى العربية",
      switch_to_english: "التبديل إلى الإنجليزية",
      switch_to_spanish: "التبديل إلى الإسبانية",
      request_quote: "اطلب عرض سعر",
      contact_us: "تواصل معنا",
      whatsapp: "تواصل عبر واتساب",
    },
    nav: {
      home: "الرئيسية",
      about: "من نحن",
      products: "المنتجات",
      brands: "العلامات التجارية",
      why: "لماذا برايم زون",
      process: "آلية العمل",
      contact: "اتصل بنا",
    },
    brand: {
      name_prefix: "برايم زون",
      name_suffix: "ش.م.ح",
    },
    hero: {
      eyebrow: "مقرّنا دبي · حضور عالمي",
      headline_a: "تجارة الجملة",
      headline_b: "للهواتف المحمولة",
      headline_c: "والإكسسوارات",
      headline_d: "من دبي إلى العالم.",
      subheadline:
        "تربط شركة برايم زون ش.م.ح تجار الجملة بمصادر موثوقة للهواتف المحمولة والإكسسوارات، وتفتح فرصاً تجارية عالمية انطلاقاً من دبي إلى الأسواق الدولية.",
      badges: [
        "المنطقة الحرة بمطار دبي",
        "شحن دولي",
        "تجارة جملة B2B",
        "هواتف متعددة العلامات",
        "توريد الإكسسوارات",
      ],
      stat_origin: { label: "المنشأ", value: "دبي، الإمارات" },
      stat_reach: { label: "الانتشار", value: "أكثر من 40 سوقاً" },
      stat_routes: { label: "الشحن", value: "خطوط عالمية" },
      hub_label: "المركز · دبي",
    },
    about: {
      eyebrow: "عن برايم زون",
      title_a: "بنيت لتجارة الجملة الجادة في",
      title_b: "قطاع الهواتف",
      title_dot: ".",
      subtitle:
        "شركة برايم زون ش.م.ح هي شركة في المنطقة الحرة بمطار دبي متخصصة في تجارة الجملة للهواتف المحمولة والإكسسوارات. نعمل مع التجار وبائعي التجزئة والموزعين والشركاء التجاريين الذين يحتاجون إلى مصادر موثوقة، وفرص سوقية تنافسية، وخدمة B2B احترافية.",
      paragraph:
        "نركّز على التواصل السريع، وتوفر السوق، والدعم التجاري الموثوق لعملاء الجملة الذين يرغبون بشراء أو بيع الهواتف المحمولة والإكسسوارات.",
      cta: "ابدأ محادثة جملة معنا",
      cards: [
        {
          title: "شركة في المنطقة الحرة بمطار دبي",
          body: "كيان مسجّل في المنطقة الحرة بمطار دبي — مهيّأ لتجارة جملة سريعة ومرتبطة دولياً.",
        },
        {
          title: "تجارة الهواتف المحمولة",
          body: "توريد وتجارة الهواتف المحمولة من أبرز العلامات العالمية.",
        },
        {
          title: "تجارة الإكسسوارات",
          body: "توريد جملة للشواحن والكابلات والأغطية والحماية.",
        },
        {
          title: "تركيز على B2B والجملة",
          body: "مصممة للتجار وبائعي التجزئة والموزعين ومتطلبات الكميات الكبيرة.",
        },
        {
          title: "نشاط محلي ودولي",
          body: "نخدم الشركاء في الإمارات وفي أهم الأسواق الدولية.",
        },
      ],
    },
    products: {
      eyebrow: "ما نتاجر به",
      title_a: "فئات صُمّمت لـ",
      title_b: "تجار الجملة",
      title_dot: ".",
      subtitle:
        "ندعم تجارة الجملة في الهواتف المحمولة والإكسسوارات والفئات المرتبطة بها حسب توفر السوق ومتطلبات العميل.",
      cards: [
        {
          title: "الهواتف المحمولة",
          body: "تجارة جملة للهواتف المحمولة من أبرز العلامات العالمية حسب التوفر.",
        },
        {
          title: "الهواتف الذكية",
          body: "توريد وتجارة أحدث موديلات الهواتف الذكية لعملاء B2B وبائعي التجزئة.",
        },
        {
          title: "إكسسوارات الهواتف",
          body: "توريد الإكسسوارات للتجار والمحلات والموزعين.",
        },
        {
          title: "الشواحن والكابلات",
          body: "تجارة جملة لمستلزمات الشحن ومنتجات الاتصال بالهاتف.",
        },
        {
          title: "الأغطية والحماية",
          body: "أغطية الهواتف وواقيات الشاشة وملحقات الحماية.",
        },
        {
          title: "طلبات الكميات الكبيرة",
          body: "دعم لمتطلبات الكميات الكبيرة والاستفسارات التجارية.",
        },
        {
          title: "الدعم التجاري",
          body: "تواصل احترافي ودعم توريد لتجار الهواتف.",
        },
        {
          title: "توفر السوق",
          body: "يعتمد توفر المنتجات على عرض السوق وظروف التجارة الحالية.",
        },
      ],
    },
    brands: {
      eyebrow: "تجارة متعددة العلامات",
      title_a: "نتاجر مع",
      title_b: "علامات عالمية",
      title_dot: ".",
      subtitle:
        "تتاجر برايم زون ش.م.ح في أبرز علامات الهواتف العالمية والإكسسوارات بحسب توفر السوق ومتطلبات العميل وظروف التوريد.",
      other: "علامات عالمية أخرى",
      disclaimer:
        "تُعرض أسماء وشعارات العلامات التجارية لأغراض تصنيف التجارة فقط. قد يختلف التوفر حسب العرض في السوق. لا تدّعي شركة برايم زون ش.م.ح أي توزيع رسمي ما لم يُذكر ذلك صراحة. جميع العلامات التجارية مملوكة لأصحابها.",
    },
    why: {
      eyebrow: "لماذا برايم زون",
      title_a: "شريك",
      title_b: "احترافي لتجارة B2B",
      title_c: "من دبي.",
      subtitle:
        "موثوق وسريع ومرتبط دولياً — مصمم لتجارة الجملة في الهواتف المحمولة والإكسسوارات.",
      cards: [
        {
          title: "شركة في المنطقة الحرة بدبي",
          body: "مقرنا في المنطقة الحرة بمطار دبي، لدعم النشاط الإقليمي والدولي.",
        },
        {
          title: "تركيز على الجملة",
          body: "مصممة للتجار وبائعي التجزئة والموزعين ومتطلبات الكميات الكبيرة.",
        },
        {
          title: "تجارة متعددة العلامات",
          body: "وصول إلى فرص تجارة الهواتف عبر أبرز العلامات حسب التوفر.",
        },
        {
          title: "توريد الإكسسوارات",
          body: "دعم احتياجات الجملة من إكسسوارات الهواتف والمنتجات المرتبطة بها.",
        },
        {
          title: "تواصل سريع",
          body: "تواصل واضح ومباشر واحترافي لعملاء الأعمال.",
        },
        {
          title: "صفقات سوقية تنافسية",
          body: "تركيز على فرص التجارة المدفوعة بالسوق وقيمة الجملة.",
        },
        {
          title: "شبكة توريد موثوقة",
          body: "نساعد العملاء في إيجاد المخزون والفرص المناسبة.",
        },
        {
          title: "حضور محلي ودولي",
          body: "نخدم مشتري الجملة وشركاءنا محلياً ودولياً.",
        },
      ],
    },
    process: {
      eyebrow: "كيف نعمل",
      title_a: "عملية استفسار",
      title_b: "بسيطة واحترافية",
      title_dot: ".",
      subtitle:
        "خمس خطوات واضحة من الاستفسار إلى التسليم — مصممة لمشتري الجملة الجادين.",
      steps: [
        {
          title: "شارك متطلباتك",
          body: "أرسل لنا نوع المنتج، العلامة، الموديل، والكمية المطلوبة.",
        },
        {
          title: "التحقق من التوفر",
          body: "نتحقق من توفر السوق والخيارات التجارية المناسبة.",
        },
        {
          title: "استلام عرض السعر",
          body: "نقدّم لك السعر وشروط التعامل بناءً على استفسارك.",
        },
        {
          title: "تأكيد الطلب",
          body: "عند الاتفاق، يتم تأكيد تفاصيل الطلب وشروط الدفع.",
        },
        {
          title: "التسليم أو الاستلام",
          body: "ننسّق الاستلام أو التسليم أو الشحن حسب الصفقة.",
        },
      ],
    },
    cta: {
      badge: "استفسار جملة",
      title_a: "هل أنت مستعد",
      title_b: "للتجارة؟",
      body: "تواصل مع برايم زون ش.م.ح لاستفسارات تجارة الجملة في الهواتف المحمولة والإكسسوارات.",
      whatsapp_btn: "تواصل معنا عبر واتساب",
    },
    contact: {
      eyebrow: "لنتواصل",
      title_a: "أرسل",
      title_b: "استفسار الجملة الخاص بك",
      title_dot: ".",
      subtitle:
        "أخبرنا بما تحتاجه — سيقوم فريقنا بالمراجعة والرد بالتوفر والأسعار والشروط.",
      company_block: {
        name: "برايم زون ش.م.ح",
        tagline: "تجارة جملة للهواتف المحمولة والإكسسوارات",
      },
      labels: {
        location: "الموقع",
        phone: "الهاتف / واتساب",
        email: "البريد الإلكتروني",
      },
      address: "6EA 121، الطابق الأول، مبنى 6 شرق أ،\nالمنطقة الحرة بمطار دبي، دبي، الإمارات",
      fields: {
        full_name: "الاسم الكامل",
        company_name: "اسم الشركة",
        country: "الدولة",
        phone: "الهاتف / واتساب",
        email: "البريد الإلكتروني",
        product_requirement: "المنتج المطلوب",
        brand_model: "العلامة / الموديل",
        quantity: "الكمية",
        message: "الرسالة",
        message_placeholder:
          "أخبرنا عن متطلباتك أو السوق المستهدف أو أي تفاصيل إضافية.",
      },
      submit: "إرسال الاستفسار",
      submitting: "جارٍ الإرسال…",
      consent:
        "بإرسالك هذا النموذج، فأنت توافق على أن تتواصل معك برايم زون ش.م.ح بخصوص استفسارك التجاري.",
      toast_success: "تم إرسال الاستفسار. سيتواصل فريقنا معك قريباً.",
      toast_validation: "يرجى تعبئة الاسم والبريد الإلكتروني والهاتف.",
      toast_error: "تعذّر إرسال الاستفسار. يرجى المحاولة مجدداً.",
      map_caption:
        "6EA 121، الطابق الأول، مبنى 6 شرق أ · المنطقة الحرة بمطار دبي، دبي، الإمارات",
      directions: "افتح في خرائط جوجل ←",
    },
    footer: {
      tagline: "تجارة جملة للهواتف المحمولة والإكسسوارات.",
      address_l1: "6EA 121، الطابق الأول، مبنى 6 شرق أ،",
      address_l2: "المنطقة الحرة بمطار دبي، دبي، الإمارات.",
      cols: {
        company: "الشركة",
        trading: "التجارة",
        contact: "الاتصال",
      },
      links: {
        about: "من نحن",
        products: "المنتجات",
        brands: "العلامات",
        why: "لماذا برايم زون",
        mobile_phones: "الهواتف المحمولة",
        smartphones: "الهواتف الذكية",
        accessories: "الإكسسوارات",
        bulk_orders: "الكميات الكبيرة",
        request_quote: "اطلب عرض سعر",
        whatsapp: "واتساب",
        email: "البريد",
        location: "الموقع",
      },
      copyright: "© 2026 برايم زون ش.م.ح. جميع الحقوق محفوظة.",
      built_for: "مصمّم لتجارة الجملة B2B.",
    },
  },
  es: {
    common: {
      lang_en: "EN",
      lang_ar: "AR",
      lang_es: "ES",
      switch_to_arabic: "Cambiar a árabe",
      switch_to_english: "Cambiar a inglés",
      switch_to_spanish: "Cambiar a español",
      request_quote: "Solicitar cotización",
      contact_us: "Contáctanos",
      whatsapp: "Chatea por WhatsApp",
    },
    nav: {
      home: "Inicio",
      about: "Nosotros",
      products: "Productos",
      brands: "Marcas",
      why: "Por qué Prime Zone",
      process: "Proceso",
      contact: "Contacto",
    },
    brand: {
      name_prefix: "Prime Zone",
      name_suffix: "FZCO",
    },
    hero: {
      eyebrow: "Con base en Dubái · Conectados globalmente",
      headline_a: "Comercio mayorista de",
      headline_b: "teléfonos móviles",
      headline_c: "y accesorios",
      headline_d: "desde Dubái al mundo.",
      subheadline:
        "Prime Zone FZCO conecta a comerciantes mayoristas con teléfonos móviles confiables, accesorios y oportunidades comerciales globales — enviando desde Dubái a mercados internacionales.",
      badges: [
        "Zona Franca del Aeropuerto de Dubái",
        "Envío Global",
        "Comercio Mayorista B2B",
        "Teléfonos Multi-Marca",
        "Suministro de Accesorios",
      ],
      stat_origin: { label: "Origen", value: "Dubái, EAU" },
      stat_reach: { label: "Alcance", value: "+40 Mercados" },
      stat_routes: { label: "Envíos", value: "Rutas Globales" },
      hub_label: "Hub · Dubái",
    },
    about: {
      eyebrow: "Acerca de Prime Zone",
      title_a: "Diseñado para el comercio mayorista serio de",
      title_b: "telefonía móvil",
      title_dot: ".",
      subtitle:
        "Prime Zone FZCO es una empresa en la Zona Franca del Aeropuerto de Dubái especializada en el comercio mayorista de teléfonos móviles y accesorios. Trabajamos con comerciantes, revendedores, distribuidores y socios comerciales que requieren un abastecimiento confiable, oportunidades de mercado competitivas y un servicio B2B profesional.",
      paragraph:
        "Nos enfocamos en la comunicación rápida, la disponibilidad del mercado y el soporte comercial confiable para clientes mayoristas que buscan comprar o vender teléfonos móviles y accesorios.",
      cta: "Inicia una conversación mayorista",
      cards: [
        {
          title: "Empresa en la Zona Franca del Aeropuerto de Dubái",
          body: "Entidad registrada en la Zona Franca del Aeropuerto de Dubái — diseñada para un comercio mayorista rápido y conectado internacionalmente.",
        },
        {
          title: "Comercio de Teléfonos Móviles",
          body: "Abastecimiento y comercio de teléfonos móviles de las principales marcas globales.",
        },
        {
          title: "Comercio de Accesorios",
          body: "Suministro mayorista de cargadores, cables, fundas y protección.",
        },
        {
          title: "Enfoque B2B Mayorista",
          body: "Diseñado para comerciantes, revendedores, distribuidores y compradores al por mayor.",
        },
        {
          title: "Negocios en EAU e Internacional",
          body: "Atendemos socios en los EAU y en los principales mercados internacionales.",
        },
      ],
    },
    products: {
      eyebrow: "Lo que comercializamos",
      title_a: "Categorías diseñadas para",
      title_b: "comerciantes mayoristas",
      title_dot: ".",
      subtitle:
        "Apoyamos el comercio mayorista en teléfonos móviles, accesorios y categorías de productos relacionadas según la disponibilidad del mercado y los requisitos del cliente.",
      cards: [
        { title: "Teléfonos Móviles", body: "Comercio mayorista de teléfonos móviles de las principales marcas globales según disponibilidad." },
        { title: "Smartphones", body: "Abastecimiento y comercio de nuevos modelos de smartphones para clientes B2B y revendedores." },
        { title: "Accesorios Móviles", body: "Suministro de accesorios para comerciantes, tiendas y distribuidores." },
        { title: "Cargadores y Cables", body: "Comercio mayorista de accesorios de carga y productos de conectividad móvil." },
        { title: "Fundas y Protección", body: "Fundas para teléfonos, protectores de pantalla y accesorios de protección relacionados." },
        { title: "Pedidos a Granel", body: "Soporte para requisitos de cantidades mayoristas y consultas comerciales." },
        { title: "Soporte Comercial", body: "Comunicación profesional y soporte de abastecimiento para comerciantes móviles." },
        { title: "Disponibilidad del Mercado", body: "La disponibilidad de productos depende del suministro actual del mercado y las condiciones comerciales." },
      ],
    },
    brands: {
      eyebrow: "Comercio Multi-Marca",
      title_a: "Comerciamos con",
      title_b: "marcas globales",
      title_dot: ".",
      subtitle:
        "Prime Zone FZCO comercia con las principales marcas globales de teléfonos móviles y accesorios según la disponibilidad del mercado, los requisitos del cliente y las condiciones de suministro mayorista.",
      other: "Otras Marcas Globales",
      disclaimer:
        "Los nombres y logotipos de las marcas se muestran únicamente como referencia de categoría comercial. La disponibilidad puede variar según el suministro del mercado. Prime Zone FZCO no reclama distribución oficial a menos que se indique específicamente. Todas las marcas registradas pertenecen a sus respectivos propietarios.",
    },
    why: {
      eyebrow: "Por qué elegir Prime Zone",
      title_a: "Un",
      title_b: "socio comercial B2B profesional",
      title_c: "desde Dubái.",
      subtitle:
        "Confiable, rápido y conectado internacionalmente — diseñado para el comercio mayorista de teléfonos móviles y accesorios.",
      cards: [
        { title: "Empresa en Zona Franca de Dubái", body: "Ubicada en la Zona Franca del Aeropuerto de Dubái, apoyando la actividad comercial regional e internacional." },
        { title: "Enfocados en Mayoreo", body: "Diseñados para comerciantes, revendedores, distribuidores y requisitos comerciales a granel." },
        { title: "Comercio Multi-Marca", body: "Acceso a oportunidades de comercio de teléfonos móviles a través de las principales marcas según disponibilidad." },
        { title: "Suministro de Accesorios", body: "Apoyamos las necesidades mayoristas de accesorios móviles y productos relacionados." },
        { title: "Comunicación Rápida", body: "Comunicación clara, directa y profesional para clientes empresariales." },
        { title: "Ofertas Competitivas del Mercado", body: "Enfocados en oportunidades comerciales impulsadas por el mercado y valor mayorista." },
        { title: "Red de Abastecimiento Confiable", body: "Ayudando a los clientes a encontrar stock y oportunidades comerciales adecuadas." },
        { title: "Alcance EAU e Internacional", body: "Atendemos a compradores y socios mayoristas locales e internacionales." },
      ],
    },
    process: {
      eyebrow: "Cómo trabajamos",
      title_a: "Un",
      title_b: "proceso de consulta",
      title_c: "simple y profesional.",
      title_dot: "",
      subtitle: "Cinco pasos claros desde la consulta hasta la entrega — diseñados para compradores mayoristas serios.",
      steps: [
        { title: "Comparte tu requerimiento", body: "Envíanos el tipo de producto, marca, modelo y cantidad que necesitas." },
        { title: "Verificación de disponibilidad", body: "Verificamos la disponibilidad del mercado y las opciones comerciales adecuadas." },
        { title: "Recibe cotización", body: "Proporcionamos precios y términos comerciales según tu consulta." },
        { title: "Confirma el pedido", body: "Una vez acordado, se confirman los detalles del pedido y los términos de pago." },
        { title: "Entrega o recogida", body: "Coordinamos la recogida, entrega o envío según el acuerdo." },
      ],
    },
    cta: {
      badge: "Consulta Mayorista",
      title_a: "¿Listo para",
      title_b: "comerciar?",
      body: "Conecta con Prime Zone FZCO para consultas de comercio mayorista de teléfonos móviles y accesorios.",
      whatsapp_btn: "Contáctanos por WhatsApp",
    },
    contact: {
      eyebrow: "Conectemos",
      title_a: "Envía tu",
      title_b: "consulta mayorista",
      title_dot: ".",
      subtitle: "Cuéntanos lo que necesitas — nuestro equipo revisará y responderá con disponibilidad, precios y términos.",
      company_block: {
        name: "Prime Zone FZCO",
        tagline: "Comercio Mayorista de Teléfonos Móviles y Accesorios",
      },
      labels: {
        location: "Ubicación",
        phone: "Teléfono / WhatsApp",
        email: "Correo electrónico",
      },
      address:
        "6EA 121, Primer Piso, Edificio 6 Este A,\nZona Franca del Aeropuerto de Dubái, Dubái, EAU",
      fields: {
        full_name: "Nombre completo",
        company_name: "Nombre de la empresa",
        country: "País",
        phone: "Teléfono / WhatsApp",
        email: "Correo electrónico",
        product_requirement: "Producto requerido",
        brand_model: "Marca / Modelo",
        quantity: "Cantidad",
        message: "Mensaje",
        message_placeholder:
          "Cuéntanos sobre tu requerimiento, mercado objetivo o cualquier detalle específico.",
      },
      submit: "Enviar consulta",
      submitting: "Enviando…",
      consent:
        "Al enviar, aceptas que Prime Zone FZCO te contacte sobre tu consulta comercial.",
      toast_success: "Consulta enviada. Nuestro equipo se pondrá en contacto contigo en breve.",
      toast_validation: "Por favor completa tu nombre, correo y teléfono.",
      toast_error: "No se pudo enviar la consulta. Por favor intenta de nuevo.",
      map_caption:
        "6EA 121, Primer Piso, Edificio 6 Este A · Zona Franca del Aeropuerto de Dubái, Dubái, EAU",
      directions: "Abrir en Google Maps →",
    },
    footer: {
      tagline: "Comercio Mayorista de Teléfonos Móviles y Accesorios.",
      address_l1: "6EA 121, Primer Piso, Edificio 6 Este A,",
      address_l2: "Zona Franca del Aeropuerto de Dubái, Dubái, EAU.",
      cols: {
        company: "Empresa",
        trading: "Comercio",
        contact: "Contacto",
      },
      links: {
        about: "Nosotros",
        products: "Productos",
        brands: "Marcas",
        why: "Por qué Prime Zone",
        mobile_phones: "Teléfonos Móviles",
        smartphones: "Smartphones",
        accessories: "Accesorios",
        bulk_orders: "Pedidos a Granel",
        request_quote: "Solicitar cotización",
        whatsapp: "WhatsApp",
        email: "Correo",
        location: "Ubicación",
      },
      copyright: "© 2026 Prime Zone FZCO. Todos los derechos reservados.",
      built_for: "Construido para comercio mayorista B2B.",
    },
  },
};

const I18nContext = createContext({
  locale: "en",
  dir: "ltr",
  t: (k) => k,
  setLocale: () => {},
});

function getInitialLocale() {
  if (typeof window === "undefined") return "en";
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && LOCALES.includes(saved)) return saved;
  return "en";
}

function getByPath(obj, path) {
  return path.split(".").reduce((acc, key) => {
    if (acc == null) return undefined;
    return acc[key];
  }, obj);
}

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(getInitialLocale);

  const dir = locale === "ar" ? "rtl" : "ltr";

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("lang", locale);
    root.setAttribute("dir", dir);
    document.body.classList.toggle("font-ar", locale === "ar");
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // ignore
    }
  }, [locale, dir]);

  const setLocale = (next) => {
    if (LOCALES.includes(next)) setLocaleState(next);
  };

  const value = useMemo(() => {
    const t = (key, fallback) => {
      const val = getByPath(dict[locale], key);
      if (val === undefined) {
        const en = getByPath(dict.en, key);
        return en !== undefined ? en : fallback ?? key;
      }
      return val;
    };
    return { locale, dir, t, setLocale };
  }, [locale, dir]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

export { LOCALES };
